class CookieConsentManager {
    constructor(config = {}) {
        this.storageKey = config.storageKey || 'cookie_consent_status';
        this.policyVersion = config.policyVersion || '1.0.0';
        this.cookieMaxAge = 60 * 60 * 24 * 365;
    }

    getConsent() {
        const rawValue = this.getRawValue();

        if (!rawValue) {
            return null;
        }

        const normalizedConsent = this.normalizeConsent(rawValue);

        if (!normalizedConsent || !this.isValidConsent(normalizedConsent)) {
            return null;
        }

        return normalizedConsent;
    }

    acceptNecessary(source = 'banner') {
        const consent = this.createConsent({
            source,
            categories: {
                necessary: true,
                analytics: false,
                marketing: false,
                preferences: false,
            },
        });

        this.persistConsent(consent);

        return consent;
    }

    acceptAll(source = 'banner') {
        const consent = this.createConsent({
            source,
            categories: {
                necessary: true,
                analytics: true,
                marketing: true,
                preferences: true,
            },
        });

        this.persistConsent(consent);

        return consent;
    }

    isCategoryAllowed(category) {
        if (category === 'necessary') {
            return true;
        }

        const consent = this.getConsent();

        if (!consent || !consent.categories) {
            return false;
        }

        return Boolean(consent.categories[category]);
    }

    emitReady() {
        window.dispatchEvent(new CustomEvent('cookieConsentReady', {
            detail: {
                consent: this.getConsent(),
            },
        }));
    }

    createConsent({ source, categories }) {
        return {
            version: this.policyVersion,
            source,
            timestamp: new Date().toISOString(),
            categories,
        };
    }

    persistConsent(consent) {
        this.setRawValue(JSON.stringify(consent));
        this.emitUpdated(consent);
    }

    emitUpdated(consent) {
        window.dispatchEvent(new CustomEvent('cookieConsentUpdated', {
            detail: {
                consent,
            },
        }));
    }

    getRawValue() {
        try {
            const localValue = localStorage.getItem(this.storageKey);

            if (localValue) {
                return localValue;
            }
        } catch (e) {
            // Fallback to cookies when localStorage is unavailable
        }

        return this.getCookieValue(this.storageKey);
    }

    setRawValue(value) {
        try {
            localStorage.setItem(this.storageKey, value);
            return;
        } catch (e) {
            // Continue with cookie fallback
        }

        const safeValue = encodeURIComponent(value);
        document.cookie = `${this.storageKey}=${safeValue}; path=/; max-age=${this.cookieMaxAge}; SameSite=Lax`;
    }

    getCookieValue(name) {
        const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const cookieRegex = new RegExp(`(?:^|; )${escapedName}=([^;]*)`);
        const match = document.cookie.match(cookieRegex);

        if (!match) {
            return null;
        }

        try {
            return decodeURIComponent(match[1]);
        } catch (e) {
            return null;
        }
    }

    normalizeConsent(rawValue) {
        if (rawValue === 'all') {
            return this.createConsent({
                source: 'legacy',
                categories: {
                    necessary: true,
                    analytics: true,
                    marketing: true,
                    preferences: true,
                },
            });
        }

        if (rawValue === 'necessary') {
            return this.createConsent({
                source: 'legacy',
                categories: {
                    necessary: true,
                    analytics: false,
                    marketing: false,
                    preferences: false,
                },
            });
        }

        try {
            const parsed = JSON.parse(rawValue);
            const categories = parsed && parsed.categories ? parsed.categories : {};

            return {
                version: parsed && parsed.version ? parsed.version : '',
                source: parsed && parsed.source ? parsed.source : 'unknown',
                timestamp: parsed && parsed.timestamp ? parsed.timestamp : '',
                categories: {
                    necessary: Boolean(categories.necessary),
                    analytics: Boolean(categories.analytics),
                    marketing: Boolean(categories.marketing),
                    preferences: Boolean(categories.preferences),
                },
            };
        } catch (e) {
            return null;
        }
    }

    isValidConsent(consent) {
        return Boolean(
            consent
            && consent.version === this.policyVersion
            && consent.timestamp
            && consent.categories
            && consent.categories.necessary === true
        );
    }
}

window.CookieConsentManager = CookieConsentManager;
